import { useEffect, useRef, useState, Suspense } from "react";
import { useAuth } from "./auth";
import NotificationBell from "./NotificationBell";
import AlertBanner from "./AlertBanner";
import { InfoTip } from "./InfoTip";
import { nid, StaleChip } from "./breakUi";
import { useMe } from "./sharedData";
import { api } from "./api";
import ErrorBoundary from "./ErrorBoundary";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import ProfileModal, { UserAvatar } from "./ProfileModal";
import { TermsGate } from "./Safety";
import { Bell, BellRing, Ban, LogOut, AlertTriangle, Settings as SettingsIcon, Coffee, CalendarDays, CalendarClock, Activity, TrendingUp, Trophy, DoorOpen, AlarmClockPlus, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

// Heavy feature modules are code-split so the initial bundle stays small —
// each loads only when its tab/module is first opened.
const Settings = lazyWithRetry(() => import("./Settings"));
const Analytics = lazyWithRetry(() => import("./Analytics"));
const Stacks = lazyWithRetry(() => import("./Stacks"));
const LogsPage = lazyWithRetry(() => import("./Logs"));
const Leaves = lazyWithRetry(() => import("./Leaves"));
const Exits = lazyWithRetry(() => import("./Exits"));
const RosterMine = lazyWithRetry(() => import("./ShiftSwaps").then((m) => ({ default: m.MySchedule })));
const MyOvertime = lazyWithRetry(() => import("./Overtime").then((m) => ({ default: m.MyOvertime })));
const Reports = lazyWithRetry(() => import("./Reports"));
const TeamStats = lazyWithRetry(() => import("./TeamStats"));
const AgentView = lazyWithRetry(() => import("./AgentView"));
const RtaConsole = lazyWithRetry(() => import("./RtaConsole"));
const Groups = lazyWithRetry(() => import("./Groups"));
const PlanningModule = lazyWithRetry(() => import("./wfm/PlanningModule"));

const PageLoading = () => (
  <div className="py-24 grid place-items-center text-sm text-gray-400" data-testid="page-loading">
    <img src="/kairos-logo-192.png" alt="" className="w-9 h-9 rounded-lg opacity-70 k-pulse-soft object-contain" />
  </div>
);

function TabButton({ id, label, active, onClick }) {
  return (
    <button
      data-testid={`tab-${id}`}
      onClick={onClick}
      className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors ${active ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
    >{label}</button>
  );
}

// ── root shell: header, tabs, routing ────────────────────────────────────────
export default function BreakManager() {
  const { logout } = useAuth();
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    if (t === "timeline") return "notifications"; // Timeline now lives under Logs
    return ["stats", "me", "rta", "leaves", "exits", "overtime", "reports", "analytics", "stacks", "notifications"].includes(t) ? t : "stats";
  });
  const [module, setModule] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return t === "groups" ? "groups" : t === "planning" ? "planning" : "floor";
  });
  const [settingsOpen, setSettingsOpen] = useState(() =>
    new URLSearchParams(window.location.search).get("tab") === "settings");
  const [profileOpen, setProfileOpen] = useState(false);
  const [avatarV, setAvatarV] = useState(0);
  const [groupUnread, setGroupUnread] = useState(0);
  const [me, reloadMe, meStale] = useMe(15000);
  const meRef = useRef(me); meRef.current = me;
  const clearedAwayOnce = useRef(false);
  const canGroups = !!me?.capabilities?.["groups.view"];
  useEffect(() => {
    window.__kairosUser = me ? { id: me.id, role: me.role } : null;
  }, [me]);
  // Auto-clear a manual "Away" the moment you reopen / refocus the app (3b).
  useEffect(() => {
    if (me && !clearedAwayOnce.current) {
      clearedAwayOnce.current = true;
      if (me.presence_status === "away") api.post("/presence", { status: "active" }).then(reloadMe).catch(() => {});
    }
  }, [me, reloadMe]);
  useEffect(() => {
    const clearAway = () => {
      if (meRef.current?.presence_status === "away") api.post("/presence", { status: "active" }).then(reloadMe).catch(() => {});
    };
    const onVis = () => { if (document.visibilityState === "visible") clearAway(); };
    window.addEventListener("focus", clearAway);
    document.addEventListener("visibilitychange", onVis);
    return () => { window.removeEventListener("focus", clearAway); document.removeEventListener("visibilitychange", onVis); };
  }, [reloadMe]);
  useEffect(() => {
    if (!canGroups) return;
    let t;
    const load = () => api.get("/groups/unread-count").then((r) => setGroupUnread(r.data.total)).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    const h = () => { clearTimeout(t); t = setTimeout(load, 800); };
    window.addEventListener("kairos:group-message", h);
    window.addEventListener("kairos:group-read", h);
    return () => { clearInterval(iv); clearTimeout(t); window.removeEventListener("kairos:group-message", h); window.removeEventListener("kairos:group-read", h); };
  }, [canGroups, module]);
  const prevStatus = useRef(null);

  // Item 0 — keep the current page/tab across a browser refresh: mirror the
  // active view into the URL (?tab=…) so a reload restores exactly where you were.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const val = settingsOpen ? "settings" : module === "planning" ? "planning" : module === "groups" ? "groups" : tab;
    if (params.get("tab") !== val) {
      params.set("tab", val);
      window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
    }
  }, [tab, module, settingsOpen]);

  useEffect(() => {
    const s = me?.open_request?.status ?? null;
    if (prevStatus.current && prevStatus.current !== "APPROVED" && s === "APPROVED") {
      try {
        if ("Notification" in window && Notification.permission === "granted")
          new Notification("Break approved", { body: `${me.open_request.decided_by_name ? `Approved by ${me.open_request.decided_by_name}. ` : ""}Start your ${me.open_request.break_type_name} soon.` });
      } catch (e) {}
    }
    prevStatus.current = s;
  }, [me?.open_request?.status, me?.open_request?.break_type_name]);

  if (!me) return (
    <div className="k-shell min-h-screen grid place-items-center">
      <div className="relative z-10 flex flex-col items-center gap-3 text-gray-400 text-sm">
        <img src="/kairos-logo-192.png" alt="Kairos" className="w-11 h-11 rounded-xl shadow-lg k-pulse-soft object-contain" />
        Loading your workspace…
      </div>
    </div>
  );
  const roles = me.roles;
  const caps = me.capabilities || {};
  const canSettings = !!(caps["users.manage"] || caps["config.edit"] || caps["roster.import"] || caps["roster.stagger"] || caps["logs.admin_view"] || caps["digest.manage"] || roles.super_admin);
  const noRole = !caps["break.request"] && !caps["queue.view"] && !canSettings && !caps["analytics.view"];

  const openTab = (t) => { setModule("floor"); setSettingsOpen(false); setTab(t); };

  const canBreaksTab = !!caps["break.request"];
  const canLeavesTab = !!(caps["leave.apply"] || caps["leave.approve"] || caps["leave.tracker"] || caps["leave.admin"]);
  const canRosterTab = canBreaksTab;
  const canOvertimeTab = canBreaksTab;
  const canExitsTab = !!caps["exit.raise"];
  const inUserDen = tab === "me" || tab === "leaves" || tab === "roster" || tab === "overtime" || tab === "exits";
  const firstUserDenTab = canBreaksTab ? "me" : canLeavesTab ? "leaves" : canRosterTab ? "roster" : "exits";
  const canRtaTab = !!caps["queue.view"];
  const canAnalyticsTab = !!caps["analytics.view"];
  const canStacksTab = !!caps["stacks.view"];
  const canReportsTab = !!caps["reports.view"];
  const canApproveOt = !!(roles.supervisor || roles.wfm);
  const inRtmaDen = tab === "rta" || tab === "analytics" || tab === "stacks";

  return (
    <div className="k-shell min-h-screen flex flex-col text-gray-900">
      {!me.tos_accepted && <TermsGate onAccepted={reloadMe} />}
      <div className="brand-bar h-1 w-full relative z-10" />
      <AlertBanner onOpenTab={openTab} />
      <header className="relative z-20 bg-white/80 backdrop-blur-xl border-b border-gray-100 sticky top-0">
        <div className="max-w-[1600px] mx-auto w-full px-5 lg:px-8 2xl:px-12 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/kairos-logo-192.png" alt="Kairos" data-testid="header-brand-logo" className="w-10 h-10 rounded-xl shadow-md object-contain" />
          <button data-testid="header-profile-btn" onClick={() => setProfileOpen(true)} title="My profile"
            className="flex items-center gap-2.5 text-left rounded-xl px-1.5 py-1 -ml-1 hover:bg-indigo-50/70 transition-colors">
            <UserAvatar key={avatarV} id={me.id} name={me.full_name} hasPhoto size="w-8 h-8" text="text-[10px]" />
            <span>
              <span className="block font-bold text-gray-900 leading-tight tracking-tight">Kairos <span className="text-xs font-normal text-gray-400">· zefficacy</span></span>
              <span data-testid="current-user-name" className="block text-xs text-gray-400 leading-tight">{nid(me.full_name, me.cisco_id)} · {me.team || me.role.replace("_", " ")}</span>
            </span>
          </button>
        </div>
        <div className="flex items-center gap-3">
          <StaleChip show={meStale} />
          {(caps["wfm.view"] || caps["groups.view"]) && (
            <div className="flex items-center gap-0.5 bg-indigo-50/80 border border-indigo-100 rounded-lg p-0.5">
              {[["floor", "Floor", true], ["planning", "Planning", !!caps["wfm.view"]], ["groups", "Groups", !!caps["groups.view"]]].filter(([, , show]) => show).map(([id, label]) => (
                <button key={id} data-testid={`module-${id}`} onClick={() => { setModule(id); setSettingsOpen(false); }}
                        className={`relative px-3 py-1 text-xs font-bold rounded-md transition-colors ${module === id && !settingsOpen ? "bg-indigo-600 text-white shadow-sm" : "text-indigo-500 hover:text-indigo-800"}`}>
                  {label}
                  {id === "groups" && groupUnread > 0 && (
                    <span data-testid="module-groups-badge" className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center shadow-sm">{groupUnread > 99 ? "99+" : groupUnread}</span>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-1"><NotifyButton /><InfoTip testid="info-alerts" side="bottom" text="Turn on browser notifications so Kairos can alert you the moment your break is approved — no need to keep watching this tab. Uses your browser's permission prompt." /></div>
          <nav className={`flex items-center gap-1 bg-gray-100/80 rounded-lg p-1 ${module !== "floor" ? "hidden" : ""}`}>
            {caps["stats.view"] && <TabButton id="stats" label="Team Stats" active={tab === "stats" && !settingsOpen} onClick={() => { setTab("stats"); setSettingsOpen(false); }} />}
            {(canBreaksTab || canLeavesTab || canExitsTab) && (
              <button
                data-testid="tab-userden"
                onClick={() => { setTab(firstUserDenTab); setSettingsOpen(false); }}
                className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors ${inUserDen && !settingsOpen ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
              >User Den</button>
            )}
            {(canRtaTab || canAnalyticsTab || canStacksTab) && (
              <button
                data-testid="tab-rtmaden"
                onClick={() => { setTab(canRtaTab ? "rta" : canAnalyticsTab ? "analytics" : "stacks"); setSettingsOpen(false); }}
                className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors ${inRtmaDen && !settingsOpen ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
              >RTMA Den</button>
            )}
            {canReportsTab && <TabButton id="reports" label="Reports" active={tab === "reports" && !settingsOpen} onClick={() => { setTab("reports"); setSettingsOpen(false); }} />}
            <TabButton id="notifications" label="Logs" active={tab === "notifications" && !settingsOpen} onClick={() => { setTab("notifications"); setSettingsOpen(false); }} />
          </nav>
          <div className="flex items-center gap-2">
            {canSettings && (
              <button data-testid="header-settings-btn" onClick={() => setSettingsOpen(!settingsOpen)} title="Settings"
                className={`inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors ${settingsOpen ? "bg-indigo-600 text-white shadow-sm" : "bg-gray-100/80 text-gray-500 hover:bg-indigo-50 hover:text-indigo-600"}`}>
                <SettingsIcon size={16} />
              </button>
            )}
            <NotificationBell onOpenTab={openTab} />
            <button data-testid="presence-toggle" data-presence={me.presence_status === "away" ? "away" : "active"}
              onClick={() => api.post("/presence", { status: me.presence_status === "away" ? "active" : "away" }).then(reloadMe).catch(() => {})}
              title={me.presence_status === "away" ? "You're set to Away — click to go Active" : "You're Active — click to set yourself Away"}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg bg-gray-100/80 text-xs font-bold text-gray-600 hover:bg-gray-200 transition-colors">
              <span className={`w-2 h-2 rounded-full ${me.presence_status === "away" ? "bg-amber-400" : "bg-emerald-500"}`} />
              {me.presence_status === "away" ? "Away" : "Active"}
            </button>
            <button data-testid="logout-btn" onClick={logout} className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-rose-600 font-medium transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        </div>
      </header>

      <main className={`relative z-10 flex-1 w-full mx-auto py-7 ${module === "groups" && !settingsOpen ? "max-w-none px-4 lg:px-6" : "max-w-[1600px] px-5 lg:px-8 2xl:px-12"}`}>
        {noRole && (
          <div data-testid="no-role-banner" className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
            <AlertTriangle size={16} /> You don't have a Kairos role yet. Ask your admin to set you as an Agent or RTA.
          </div>
        )}
        <div key={settingsOpen ? "settings" : module !== "floor" ? module : tab} className="k-fade-up">
          <ErrorBoundary name="page" resetKey={settingsOpen ? "settings" : module !== "floor" ? module : tab}>
          <Suspense fallback={<PageLoading />}>
          {settingsOpen && canSettings ? (
            <Settings me={me} />
          ) : module === "planning" && caps["wfm.view"] ? (
            <PlanningModule me={me} caps={caps} />
          ) : module === "groups" && caps["groups.view"] ? (
            <Groups me={me} />
          ) : (<>
          {tab === "stats" && caps["stats.view"] && <TeamStats me={me} />}
          {inUserDen && (canBreaksTab || canLeavesTab || canExitsTab) && (
            <div className="space-y-5" data-testid="userden-page">
              {[canBreaksTab, canLeavesTab, canRosterTab, canOvertimeTab, canExitsTab].filter(Boolean).length > 1 && (
                <div className="inline-flex gap-1 bg-gray-100/80 rounded-lg p-1">
                  {canBreaksTab && <button data-testid="userden-sub-breaks" onClick={() => setTab("me")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === "me" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}><Coffee size={14} /> Breaks</button>}
                  {canLeavesTab && <button data-testid="userden-sub-leaves" onClick={() => setTab("leaves")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === "leaves" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}><CalendarDays size={14} /> Leaves</button>}
                  {canRosterTab && <button data-testid="userden-sub-roster" onClick={() => setTab("roster")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === "roster" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}><CalendarClock size={14} /> Roster</button>}
                  {canOvertimeTab && <button data-testid="userden-sub-overtime" onClick={() => setTab("overtime")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === "overtime" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}><AlarmClockPlus size={14} /> Overtime</button>}
                  {canExitsTab && <button data-testid="userden-sub-exits" onClick={() => setTab("exits")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === "exits" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}><DoorOpen size={14} /> Exits</button>}
                </div>
              )}
              {tab === "me" && canBreaksTab && <AgentView me={me} />}
              {tab === "leaves" && (caps["leave.apply"] || caps["leave.approve"] || caps["leave.admin"]) && <Leaves me={me} />}
              {tab === "roster" && canRosterTab && (
                <div className="space-y-3" data-testid="userden-roster-page">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2"><CalendarClock size={16} className="text-indigo-500" /> My roster &amp; shift swaps</h3>
                  <RosterMine />
                </div>
              )}
              {tab === "overtime" && canOvertimeTab && <MyOvertime />}
              {tab === "exits" && canExitsTab && <Exits me={me} />}
            </div>
          )}
          {inRtmaDen && (canRtaTab || canAnalyticsTab || canStacksTab) && (
            <div className="space-y-5" data-testid="rtmaden-page">
              {[canRtaTab, canAnalyticsTab, canStacksTab].filter(Boolean).length > 1 && (
                <div className="inline-flex gap-1 bg-gray-100/80 rounded-lg p-1">
                  {canRtaTab && <button data-testid="rtmaden-sub-rta" onClick={() => setTab("rta")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === "rta" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}><Activity size={14} /> RTMA Console</button>}
                  {canAnalyticsTab && <button data-testid="rtmaden-sub-analytics" onClick={() => setTab("analytics")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === "analytics" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}><TrendingUp size={14} /> Analytics</button>}
                  {canStacksTab && <button data-testid="rtmaden-sub-stacks" onClick={() => setTab("stacks")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === "stacks" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}><Trophy size={14} /> Stacks</button>}
                </div>
              )}
              {tab === "rta" && canRtaTab && <RtaConsole canApprove={!!caps["queue.approve"]} canOverride={!!caps["queue.override"]} canAssign={!!caps["breaks.assign"]} canFreeze={!!caps["freeze.manage"]} canSwaps={!!caps["swaps.decide"]} canOt={!!caps["ot.manage"]} canApproveOt={canApproveOt} />}
              {tab === "analytics" && canAnalyticsTab && <Analytics />}
              {tab === "stacks" && canStacksTab && <Stacks me={me} />}
            </div>
          )}
          {tab === "reports" && canReportsTab && <Reports />}
          {tab === "notifications" && <LogsPage canBroadcasts={!!caps["logs.broadcasts"]} canTimeline={!!caps["timeline.view"]} me={me} />}
          </>)}
          </Suspense>
          </ErrorBoundary>
        </div>
      </main>

      <footer className="relative z-10 text-center text-[10px] text-gray-400 py-2 border-t border-gray-100">Kairos · zefficacy</footer>
      {profileOpen && <ProfileModal me={me} onClose={() => setProfileOpen(false)} onPhotoChanged={() => setAvatarV((v) => v + 1)} />}
    </div>
  );
}

function NotifyButton() {
  const [perm, setPerm] = useState("Notification" in window ? Notification.permission : "unsupported");
  if (perm === "unsupported") return null;
  if (perm === "granted") return <span className="text-xs text-emerald-600 font-medium hidden sm:flex items-center gap-1.5"><BellRing size={14} /> Alerts on</span>;
  const click = () => {
    if (Notification.permission === "denied") {
      toast.error("Desktop alerts are blocked for this site in your browser. Click the lock/tune icon next to the address bar → Site settings → Notifications → Allow, then reload.", { duration: 12000 });
      setPerm("denied");
      return;
    }
    Notification.requestPermission().then((p) => {
      setPerm(p);
      if (p === "granted") {
        toast.success("Desktop alerts enabled — you'll get pop-ups even when Kairos is in another tab.");
        try { new Notification("Kairos alerts enabled", { body: "You'll be notified about approvals, queue updates and break reminders here." }); } catch { /* noop */ }
      } else if (p === "denied") {
        toast.error("You blocked notifications. To enable later: lock icon in the address bar → Notifications → Allow.", { duration: 10000 });
      } else {
        toast.info("Permission prompt dismissed — click Enable alerts again when ready.");
      }
    });
  };
  if (perm === "denied") {
    return (
      <button data-testid="enable-alerts-btn" onClick={click} title="Notifications are blocked in your browser for this site" className="text-xs font-medium text-rose-500 hover:text-rose-700 hidden sm:flex items-center gap-1.5 transition-colors">
        <Ban size={13} /> Alerts blocked — how to fix
      </button>
    );
  }
  return (
    <button
      data-testid="enable-alerts-btn"
      onClick={click}
      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hidden sm:flex items-center gap-1.5 transition-colors"
    ><Bell size={14} /> Enable alerts</button>
  );
}
